import type { OrderStatus } from '@lds-efsp/shared-contracts';

/**
 * The order-status vocabulary's single owner: the kebab-case status → display
 * config (badge color + label), kept out of the badge component so surfaces
 * that label a status without rendering a badge — the admin orders list's
 * Status column filter options — derive from the same source instead of
 * restating it.
 */
interface StatusConfig {
  bg: string;
  text: string;
}

interface OrderStatusBadgeConfig extends StatusConfig {
  label: string;
}

// Exhaustive map from every OrderStatus enum value to an explicit (non-gray-default)
// badge label + color. Typing as Record<OrderStatus, ...> makes the compiler enforce
// an entry for all 11 values, so a kebab-case status from the order pages never falls
// through to the gray default. No bg here may be the gray default '#F3F4F6'.
export const ORDER_STATUS_CONFIG: Record<OrderStatus, OrderStatusBadgeConfig> = {
  'draft': { bg: '#E5E7EB', text: '#374151', label: 'Draft' },
  'submitted': { bg: '#DBEAFE', text: '#1E40AF', label: 'Submitted' },
  'in-progress': { bg: '#DBEAFE', text: '#1E40AF', label: 'In Progress' },
  'pending-review': { bg: '#FEF3C7', text: '#92400E', label: 'Pending Review' },
  'accepted': { bg: '#D1FAE5', text: '#065F46', label: 'Accepted' },
  'rejected': { bg: '#FEE2E2', text: '#991B1B', label: 'Rejected' },
  'completed': { bg: '#E0E7FF', text: '#3730A3', label: 'Completed' },
  'cancelled': { bg: '#E5E7EB', text: '#374151', label: 'Cancelled' },
  'submission-in-doubt': { bg: '#FEF3C7', text: '#92400E', label: 'Submission Pending Confirmation' },
  'escalated': { bg: '#FFEDD5', text: '#9A3412', label: 'Needs Attention' },
  'ready-for-review': { bg: '#FEF3C7', text: '#92400E', label: 'Ready for Review' },
  'partially-accepted': { bg: '#FEF3C7', text: '#92400E', label: 'Partially Accepted' },
};

// The kebab-case status → display-label half of ORDER_STATUS_CONFIG, for
// surfaces that label a status without rendering a badge (the admin orders
// list's Status column filter options). Derived from the same object the
// badge renders, so the label vocabulary has one owner.
export const ORDER_STATUS_LABELS: Readonly<Record<OrderStatus, string>> = Object.fromEntries(
  Object.entries(ORDER_STATUS_CONFIG).map(([status, config]) => [status, config.label]),
) as Readonly<Record<OrderStatus, string>>;
